import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';

/**
 * One-shot handoff between the account-wide candidate search and the
 * `jobApplicantId` deep-link flow (ONE-6912 "Jump to any candidate").
 *
 * Selecting a search result only *starts* a jump: `router.push` resolves as
 * soon as the URL changes, while the landing is resolved asynchronously by
 * `useJobsRouting`'s route watcher. The confirmation snackbar and the
 * jump-succeeded/failed analytics therefore must not fire at selection time —
 * the search registers a pending jump here, and the routing flow confirms or
 * fails it once the landing actually settles.
 */
export interface PendingGlobalSearchJump {
  /** The applicationId (jobApplicantId) the deep link is resolving. */
  applicationId: number;
  /**
   * Pre-escaped confirmation markup for the snackbar (Snackbar.vue renders
   * its text with v-html).
   */
  message: string;
  /**
   * The route (fullPath) the recruiter was on when the jump started. A failed
   * landing restores it so the search's promise of exact restore holds and the
   * unresolved `jobApplicantId` query never strands them on a dead URL.
   */
  returnPath: string;
  /** Dimensions shared by the jump-succeeded / jump-failed events. */
  tracking: Record<string, unknown>;
}

/**
 * A pending jump only lives as long as a landing plausibly can. Past this it
 * is stale — an unrelated later deep link with the same jobApplicantId must
 * never consume it (stale "Jumped to…" snackbar, ancient returnPath).
 */
export const PENDING_GLOBAL_SEARCH_JUMP_TTL_MS = 30_000;

let pendingJump: PendingGlobalSearchJump | null = null;
let pendingJumpRegisteredAt = 0;

export function setPendingGlobalSearchJump(jump: PendingGlobalSearchJump): void {
  pendingJump = jump;
  pendingJumpRegisteredAt = Date.now();
}

export function clearPendingGlobalSearchJump(): void {
  pendingJump = null;
}

/**
 * Silently drops a pending jump whose landing was superseded (aborted signal):
 * no snackbar, no analytics. Returns true when a matching jump was discarded.
 */
export function discardPendingGlobalSearchJump(applicationId: number): boolean {
  if (!pendingJump || pendingJump.applicationId !== applicationId) return false;
  pendingJump = null;
  return true;
}

function consumePendingGlobalSearchJump(applicationId: number): PendingGlobalSearchJump | null {
  if (!pendingJump || pendingJump.applicationId !== applicationId) return null;
  const jump = pendingJump;
  pendingJump = null;
  // An entry stranded by an early watcher exit (recentlyAborted guard, a
  // superseding navigation) expires instead of waiting for a same-id victim.
  if (Date.now() - pendingJumpRegisteredAt > PENDING_GLOBAL_SEARCH_JUMP_TTL_MS) return null;
  return jump;
}

/**
 * Called by the routing flow after `handleJobApplicantRoute` resolved the
 * landing. Shows the success confirmation and tracks the successful jump.
 * No-op when no pending jump matches (ordinary deep links) or when the
 * navigation was aborted mid-flight. Returns true when a jump was confirmed.
 */
export function confirmPendingGlobalSearchJump(applicationId: number, aborted = false): boolean {
  const jump = consumePendingGlobalSearchJump(applicationId);
  if (!jump || aborted) return false;

  TrackingService.trackAction(TrackingActionName.GLOBAL_CANDIDATE_SEARCH_JUMP_SUCCEEDED, {
    ...jump.tracking,
  });
  SnackbarService.success(jump.message);
  return true;
}

/**
 * Called by the routing flow when `handleJobApplicantRoute` threw. Tracks the
 * failed jump; the routing flow's own error snackbars stay the only messaging,
 * so a failed landing never shows a success confirmation. Returns the consumed
 * jump so the routing flow can restore the pre-jump context (`returnPath`),
 * or null when the failed deep link was not a search jump.
 */
export function failPendingGlobalSearchJump(
  applicationId: number,
  error?: unknown,
): PendingGlobalSearchJump | null {
  const jump = consumePendingGlobalSearchJump(applicationId);
  if (!jump) return null;

  TrackingService.trackAction(TrackingActionName.GLOBAL_CANDIDATE_SEARCH_JUMP_FAILED, {
    ...jump.tracking,
    error_message: error instanceof Error ? error.message : undefined,
  });
  return jump;
}
